import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchJson } from "./api";
import { useTranslation } from "react-i18next";

const GOLD = "#d4a550";
const GOLD_DIM = "rgba(212,165,80,0.12)";
const GOLD_BORDER = "rgba(212,165,80,0.3)";
const BG = "#07070a";
const SURFACE = "#0f0f17";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#e8e6df";
const TEXT_DIM = "rgba(232,230,223,0.45)";

function formatDate(d, lang="it") {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString(lang==="en"?"en-GB":"it-IT", { weekday:"long", day:"numeric", month:"long", year:"numeric" });
}

function formatTime(d, lang="it") {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleTimeString(lang==="en"?"en-GB":"it-IT", { hour:"2-digit", minute:"2-digit" });
}

function isPast(d) {
  return new Date(d) < new Date();
}

export default function EventsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "it";
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming"); // upcoming | past

  useEffect(() => {
    fetchJson("/events")
      .then(d => { setEvents(d.events || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = events.filter(e => filter === "upcoming" ? !isPast(e.event_date) : isPast(e.event_date));

  return (
    <div style={{ minHeight:"auto", background:BG, color:TEXT, fontFamily:"'Kanit','Inter',sans-serif", padding:"32px 24px" }}>
      
      {/* Header */}
      <div style={{ marginBottom:32 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:GOLD, marginBottom:8 }}>
          {t("events.eyebrow")}
        </div>
        <h1 style={{ margin:"0 0 16px 0", fontSize:26, fontWeight:800, color:TEXT }}>{t("events.title")}</h1>
        
        {/* Intro */}
        <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"20px 24px", marginBottom:24 }}>
          <p style={{ margin:0, fontSize:13, color:TEXT_DIM, lineHeight:1.7 }}>
            {t("events.intro")}
          </p>
        </div>

        {/* Filtri + CTA */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
          <div style={{ display:"flex", gap:8 }}>
            {[["upcoming",t("events.filter_upcoming")],["past",t("events.filter_past")]].map(([k,l]) => (
              <button key={k} onClick={() => setFilter(k)}
                style={{ background:filter===k?GOLD_DIM:"none", border:`1px solid ${filter===k?GOLD_BORDER:BORDER}`, borderRadius:20, color:filter===k?GOLD:TEXT_DIM, fontSize:12, fontWeight:600, padding:"5px 14px", cursor:"pointer" }}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={() => nav("/eventi/new")}
            style={{ background:GOLD_DIM, border:`1px solid ${GOLD_BORDER}`, borderRadius:8, color:GOLD, fontSize:13, fontWeight:700, padding:"8px 18px", cursor:"pointer" }}>
            {t("events.new_event_btn")}
          </button>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign:"center", color:TEXT_DIM, fontSize:13, padding:40 }}>{t("events.loading")}</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", color:TEXT_DIM, fontSize:13, padding:40 }}>
          {filter === "upcoming" ? t("events.empty_upcoming") : t("events.empty_past")}
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {filtered.map(e => (
            <div key={e.id} onClick={() => nav(`/eventi/${e.id}`)}
              style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"20px 24px", cursor:"pointer", transition:"border-color 0.15s" }}
              onMouseEnter={el => el.currentTarget.style.borderColor = GOLD_BORDER}
              onMouseLeave={el => el.currentTarget.style.borderColor = BORDER}>
              
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:15, fontWeight:700, color:TEXT, marginBottom:4 }}>{e.title}</div>
                  <div style={{ fontSize:12, color:TEXT_DIM }}>
                    {formatDate(e.event_date, lang)} · {formatTime(e.event_date, lang)}
                    {e.city && ` · ${e.city}`}
                  </div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                  {isPast(e.event_date) ? (
                    <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:"rgba(232,230,223,0.3)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:20, padding:"2px 8px" }}>{t("events.badge_expired")}</span>
                  ) : (
                    <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:"#4ade80", border:"1px solid rgba(74,222,128,0.3)", borderRadius:20, padding:"2px 8px" }}>{t("events.badge_open")}</span>
                  )}
                  {Number(e.is_joined) === 1 && (
                    <span style={{ fontSize:10, color:GOLD }}>{t("events.joined")}</span>
                  )}
                </div>
              </div>

              {e.visibility === 'roles' && e.visibility_filter?.roles?.length > 0 && (
                <div style={{ fontSize:11, color:"rgba(212,165,80,0.7)", marginBottom:6 }}>
                  {t("events.restricted")}{e.visibility_filter.roles.join(", ")}
                </div>
              )}
              {e.description && (
                <div style={{ fontSize:12, color:TEXT_DIM, lineHeight:1.6, marginBottom:8 }}>
                  {e.description.length > 120 ? e.description.slice(0,120) + "…" : e.description}
                </div>
              )}

              <div style={{ display:"flex", alignItems:"center", gap:12, fontSize:11, color:TEXT_DIM }}>
                <span>👥 {e.participant_count} {t("events.participants")}</span>
                {!e.is_anonymous && e.creator_nome && (
                  <span>{t("events.organized_by")} {e.creator_nome} {e.creator_cognome}</span>
                )}
                {e.is_anonymous && (
                  <span>{t("events.anon_organizer")}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
